import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, withMeta } from '../common';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** auth.* — kernel router: session lifecycle (SDD §17.1: OIDC redirect + callback on /auth/*, session cookie, refresh rotation
 *  with reuse detection). The sandbox dev-login exists only so the simulated stack can run without a provider. */
export const RoleSchema = z.enum(['trader', 'admin', 'investor-viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const AuthSessionSchema = withMeta({
  userId: IdSchema,
  role: RoleSchema,
  email: z.email(),
  issuedAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  /** Device binding id the WS/voice tickets are bound to. */
  deviceId: IdSchema,
});
export type AuthSession = z.infer<typeof AuthSessionSchema>;

export const authProcedures = defineProcedures({
  'auth.session.current': query(
    'Current session (null when signed out)',
    z.void(),
    AuthSessionSchema.nullable(),
  ),
  'auth.session.login': mutation(
    'Sandbox dev-login (non-production only; the live binding is OIDC on /auth/*)',
    z.object({ email: z.email(), deviceId: IdSchema.optional() }).strict(),
    AuthSessionSchema,
  ),
  'auth.session.logout': mutation('Revoke the current session', z.void(), withMeta({ ok: z.literal(true) })),
  'auth.session.refresh': mutation(
    'Rotate the refresh token (reuse detection revokes the family)',
    z.void(),
    AuthSessionSchema,
  ),
  'auth.oidc.start': query(
    'Start an OIDC login: returns the provider redirect URL',
    z.object({ provider: z.enum(['google']), returnTo: z.string().optional() }).strict(),
    withMeta({ redirectUrl: z.string(), state: z.string() }),
  ),
});
export type AuthFamilyClient = RouterShape<typeof authProcedures>;

export const authFamily = defineFamily({
  name: 'auth',
  title: 'Auth (kernel)',
  screens: ['Inherited baseline (auth required on every row)', 'Settings'],
  procedures: authProcedures,
  binding: 'simulated',
});
